// The browser side of the field-by-field prediction (src/util/fieldwise.ts): the server's
// evidence turned into sources the form can read, kept for this configuration, and the two
// predictions -- the first page at launch, the later pages once the housing is chosen.
//
// A cpq2 source is its snapshot. A legacy order arrives as its chosen answers; what the form
// would show for it is those answers on the blank form with every operation run (`complete`,
// AnswersContext formCompleter), done only when a source is actually used.

import type { AnswerState, Question, Tab } from "../types";
import { parseConfigSnapshot } from "./configSnapshot";
import { choose, evidenceFor, evidenceNames, fillGaps, flatten, formShape, housingKey, productKey, reliability, toAnswers, type Choice, type Evidence, type Flat, type FormShape, type GapFill, type Model, type Source } from "./fieldwise";
import { visibleQuestionCodes } from "./validation";
import { keysOnTab, launchKeys, scoredKeys, visibleTabsInOrder } from "./prediction";
import { versioned } from "../data/dataVersion";
import type { EvidenceResponse, PoEvidence } from "./predictionClient";

let modelPromise: Promise<Model | null> | null = null;
/** The trained model (scripts/predict-fieldwise/train.ts), fetched once, with the PO source's
 *  measured reliability (scripts/predict-po/score.cjs -> po-reliability.json) folded in as the
 *  "po" source on both pages. */
export function loadModel(): Promise<Model | null> {
  if (!modelPromise) {
    // Versioned like every /data/* file: unversioned, the browser kept the first model it ever
    // fetched for a year (2026-10-19, Kevin's page vs a fresh browser).
    const po = fetch(versioned("/data/ndb/po-reliability.json"))
      .then((r) => (r.ok ? (r.json() as Promise<{ rel: Record<string, [number, number]> }>) : null))
      .catch(() => null);
    modelPromise = fetch(versioned("/data/ndb/predict-model.json"))
      .then((r) => (r.ok ? (r.json() as Promise<Model>) : null))
      .then((m) => (m && m.version === 1 && m.rel && m.everyone ? m : null))
      .then(async (m) => {
        const p = await po;
        if (m && p?.rel) for (const [k, c] of Object.entries(p.rel)) { m.rel[`p1\t${k}\tpo`] = c; m.rel[`p2\t${k}\tpo`] = c; }
        return m;
      })
      .catch(() => null);
  }
  return modelPromise;
}

// ---- the customer's PO (api/shared/predict/po-read.js, PO-PREDICTION.md) -----------------------
type PoLine = { index: number; model: string | null; flat: Flat; notes: Record<string, string> };
let po: { number: string; fileId: string; foundBy: string; lines: PoLine[]; trust: Record<string, [number, number]>; quotes: { id: string; tranid: string }[]; repeats: { id: string; tranid: string }[] } | null = null;
/** Keep the PO of the configuration open now. Its lines are flattened once; the profile's notes
 *  (a rewrite applied, an answer the customer's POs get wrong) are kept by answer key. */
export function setPoEvidence(ev: PoEvidence | null, optionOrder: Map<string, string[]>): boolean {
  if (!ev?.found || !ev.lines?.length) return false;
  po = {
    number: String(ev.poNumber || ev.fileName || ""),
    fileId: String(ev.fileId || ""),
    foundBy: String(ev.foundBy || "name"),
    trust: ev.trust ?? {},
    quotes: ev.quotes ?? [],
    repeats: ev.repeats ?? [],
    lines: ev.lines.map((l) => {
      const notes: Record<string, string> = {};
      for (const n of l.notes ?? []) notes[n.key] = n.text;
      return { index: l.index, model: l.model, flat: flatten(l.answers, optionOrder), notes };
    }),
  };
  return true;
}
export const hasPo = () => !!po?.lines.length;
/** What the PO did on this configuration, for the banner and the Save & Submit feedback. */
export type PoUse = { number: string; fileId: string; foundBy: string; line: number; lines: number; model: string | null; housingDiffers: { key: string; po: string; chosen: string }[]; filled: string[]; conflicts: { key: string; po: string; other: string; took: "po" | "history" }[]; skipped: string[] };
let poUse: PoUse | null = null;
export const currentPoUse = () => poUse;
/** The PO line's answers the configuration was compared with at Save & Submit. */
export const currentPoLine = (): Flat | null => (po && poUse ? po.lines.find((l) => l.index === poUse!.line)?.flat ?? null : null);
/** Fired when a PO lands after the launch: PredictionRefresh predicts the later pages again,
 *  and the PO banner appears. */
export const PO_READY_EVENT = "cpq2:po-ready";
/** The PO line this configuration is: the one agreeing most with the form (the rep's answers and
 *  the history's), ties to the first. A PO with three burners is three lines; the rep's own
 *  heat duty, firetube or housing picks among them. */
function poLine(current: Flat): (PoLine & { label: string; n: number }) | null {
  if (!po?.lines.length) return null;
  let best = 0, bestScore = -Infinity;
  po.lines.forEach((l, i) => {
    let s = 0;
    for (const [k, v] of Object.entries(l.flat)) if (current[k] !== undefined && current[k] !== "") s += current[k] === v ? 1 : -1;
    if (s > bestScore) { best = i; bestScore = s; }
  });
  const n = po.lines.length;
  return { ...po.lines[best], n, label: `PO ${po.number}${n > 1 ? ` (line ${best + 1} of ${n})` : ""}` };
}
type PoNotes = Record<string, { extra: string }>;
/** Answers a PO states but is often wrong about on the measured POs (bolt hole size 9/16 for a
 *  built 5/8, a 4" neck for none: Cimarron MRL0018637, 2026-10-19): the PO is a VOTE on these,
 *  weighed by its reliability, never the answer on its own. Everything else the PO states wins
 *  outright (Kevin, 2026-10-19: "po is stronger than history"). */
const PO_VOTE_ONLY = new Set(["BOLT_HOLE_SIZE", "NECK_HEIGHT"]);
/** The housing choice and what comes with the row picked: never set from the PO. */
const PO_NEVER = new Set(["HOUSING", "DIMENSIONS", "CELL_OD", "CELL_OD2", "CELL_OD3", "DESIGN", "BURNER_TYPE", "CALCULATE2", "HOUSING_VALIDATION", "SELECTION", "MODEL"]);
/** What the PO's model says about the housing, compared with the one the rep picked. */
const PO_MODEL_KEYS = ["HOUSING/HOUSING", "CELL_OD", "DIMENSIONS/DIMENSIONS"];
const qOfKey = (k: string) => (k.indexOf("/") < 0 ? k : k.slice(0, k.indexOf("/")));
/** Add the PO (and the quote it cites) to a page's evidence; the PO wins wherever it states an
 *  answer. Where the PO and the history disagree, both are said on hover and in the banner. */
function withPo(page: "p1" | "p2", fw: Fieldwise, ev: Evidence, c: Choice, current: Flat, onPage: (k: string) => boolean): { c: Choice; notes: PoNotes } {
  const notes: PoNotes = {};
  const line = poLine({ ...c.values, ...current });
  if (!line || !po) return { c, notes };
  const f: Flat = {};
  // Never the order type (the customer's own cpq2 configurations decide it) or anything the
  // operations compute.
  // Nor the housing: it is the rep's choice from Generate (Kevin: "Do not select the burner"), and
  // the cell OD, height and design come with the row picked. Where the chosen housing is not the
  // PO's model, the banner says so instead.
  for (const [k, v] of Object.entries(line.flat)) if (onPage(k) && k !== "ORDER_TYPE" && !PO_NEVER.has(qOfKey(k))) f[k] = v;
  const mismatch = PO_MODEL_KEYS.filter((k) => line.flat[k] !== undefined && current[k] !== undefined && current[k] !== "" && current[k] !== line.flat[k]);
  const names = evidenceNames.get(ev) ?? (evidenceNames.set(ev, {}), evidenceNames.get(ev)!);
  // The Flameco quote the PO cites, where the evidence holds it: a vote, never an override.
  for (const q of po.quotes) {
    const s = fw.sources.find((x) => x.id === q.id);
    if (!s) continue;
    const qf: Flat = {};
    for (const [k, v] of Object.entries(s.kind === "legacy" ? s.keys : s.flat)) if (onPage(k) && k !== "ORDER_TYPE") qf[k] = v;
    if (Object.keys(qf).length) { ev.poQuote = qf; names.poQuote = q.tranid; }
    break;
  }
  // The order the PO says this one repeats ("repeat of Po 70797"): the customer's own earlier
  // configuration, as recorded, a strong vote on every answer it carries. Not the housing: that
  // is still the rep's pick.
  for (const r of po.repeats) {
    const s = fw.sources.find((x) => x.id === r.id);
    if (!s) continue;
    const rf: Flat = {};
    for (const [k, v] of Object.entries(s.kind === "legacy" ? s.keys : s.flat)) if (onPage(k) && k !== "ORDER_TYPE" && !PO_NEVER.has(qOfKey(k))) rf[k] = v;
    if (Object.keys(rf).length) { ev.poRepeat = rf; names.poRepeat = r.tranid; }
    break;
  }
  if (!Object.keys(f).length && !ev.poQuote && !ev.poRepeat) return { c, notes };
  if (Object.keys(f).length) { ev.po = f; names.po = line.label; }
  // This customer's own record on each answer counts on top of everyone's.
  const rel = (k: string) => reliability(fw.model.rel, page, k, "po", po!.trust[k]);
  const relOf = (k: string, s: string) => (s === "po" ? rel(k) : reliability(fw.model.rel, page, k, s));
  const voted = choose(page, ev, relOf, onPage, fw.groupOf);
  const skipped: string[] = [];
  const filled: string[] = [];
  const conflicts: PoUse["conflicts"] = [];
  const say = (k: string, v: string) => fw.labelOf(k, v);
  for (const [k, v] of Object.entries(f)) {
    const other = c.values[k];
    const otherFrom = c.from[k];
    const disagree = other !== undefined && other !== "" && other !== v;
    const profileNote = line.notes[k] ? ` ${line.notes[k]}` : "";
    if (PO_VOTE_ONLY.has(qOfKey(k))) {
      // A vote already cast inside choose() above: take whatever the vote decided.
      if (voted.values[k] === v) filled.push(k);
      else if (voted.values[k] !== undefined) {
        conflicts.push({ key: k, po: v, other: voted.values[k], took: "history" });
        notes[k] = { extra: ` The customer's ${line.label} says ${say(k, v)}; POs have often been wrong on this, so the history's ${say(k, voted.values[k])}.${profileNote}` };
      }
      continue;
    }
    // The PO is this order's own document: where it states an answer, it wins (Kevin,
    // 2026-10-19: "po is stronger than history"). Until then it won only where POs had been
    // right 80% of the time on that answer, and a field where it and the history disagreed was
    // marked amber; now the hover and the banner still say what the history said, and nothing
    // is marked. A PO answer that POs are often wrong on (flange OD, offset side) keeps the
    // dashed "unsure" mark through its confidence, like any other weak answer.
    voted.values[k] = v; voted.from[k] = "po"; voted.confidence[k] = rel(k);
    filled.push(k);
    if (disagree) {
      conflicts.push({ key: k, po: v, other, took: "po" });
      notes[k] = { extra: ` ${sourceWords(otherFrom, ev)} said ${say(k, other)}; the PO is this order's, so the PO.${profileNote}` };
    } else if (profileNote) notes[k] = { extra: profileNote };
  }
  for (const k of Object.keys(line.notes)) if (!(k in f) && onPage(k)) skipped.push(line.notes[k]);
  // Keep what the PO did, page by page, for the banner and the Save & Submit feedback.
  const prev = poUse && poUse.line === line.index ? poUse : null;
  poUse = {
    number: po.number, fileId: po.fileId, foundBy: po.foundBy, line: line.index, lines: line.n, model: line.model,
    housingDiffers: page === "p2" ? mismatch.map((k) => ({ key: k, po: line.flat[k], chosen: current[k] })) : prev?.housingDiffers ?? [],
    filled: [...new Set([...(prev?.filled ?? []), ...filled])],
    conflicts: [...(prev?.conflicts ?? []).filter((x) => !conflicts.some((y) => y.key === x.key)), ...conflicts],
    skipped: [...new Set([...(prev?.skipped ?? []), ...skipped])],
  };
  return { c: voted, notes };
}
function sourceWords(src: string | undefined, ev: Evidence): string {
  if (!src) return "The history";
  const names = evidenceNames.get(ev) ?? {};
  const base = src.replace(/~L$/, "").replace(/Old$/, "");
  const w = (SOURCE_WORDS[base] ?? ((n: string) => n))(names[src] ?? "");
  return w.charAt(0).toUpperCase() + w.slice(1);
}

export type Fieldwise = {
  blank: Flat;
  legacyKeys?: ReadonlySet<string>;
  product: string;
  customerId: string;
  user: string;
  model: Model;
  sources: Source[];
  pick: Source | null;
  optionOrder: Map<string, string[]>;
  optionsOf: (q: string) => string[];
  groupOf: (q: string) => string;
  onFirstPage: (k: string) => boolean;
  launch: (k: string) => boolean;
  /** Answer keys on a visible tab: what the rep can see. The later pages predict only these.
   *  Hidden state (From-Center mode and its memory, routing rule, computed areas) copied in
   *  verbatim from a vote flipped From-Center on and wiped the inlet positions (2026-10-19). */
  scored: (k: string) => boolean;
  /** An answer in the rep's words: a select's option label, a text as typed. */
  labelOf: (k: string, v: string) => string;
  /** A question (or table cell) in the rep's words. */
  keyLabel: (k: string) => string;
  /** The form's fields and what each accepts (fieldwise formShape): nothing visible is left blank. */
  shape: FormShape;
  /** The question codes the rep can see for a state of the form. */
  visible: (state: AnswerState) => ReadonlySet<string>;
  /** The blank form completed by the operations, as answers (what the launch fills over). */
  blankAnswers: AnswerState;
  /** What the gap filler did on the last prediction of each page (the console report). */
  gaps: { p1?: { filled: GapFill[]; unfilled: string[] }; p2?: { filled: GapFill[]; unfilled: string[] } };
};

let session: Fieldwise | null = null;
/** The evidence of the configuration open now (one per page load). */
export const currentFieldwise = () => session;

export async function prepareFieldwise(
  ev: EvidenceResponse,
  model: Model,
  questions: Question[],
  tabs: Tab[],
  complete: (answers: AnswerState) => AnswerState,
  who: { product: string; customerId: string; user: string }
): Promise<Fieldwise> {
  const optionOrder = new Map<string, string[]>(
    questions.map((q) => [q.custrecord_cpqc_question_code, (q._answers ?? []).map((a) => a.custrecord_cpqc_answer_code)])
  );
  const optionsOf = (q: string) => optionOrder.get(q) ?? [];
  const groups = new Map<string, string>(questions.map((q) => [q.custrecord_cpqc_question_code, String(q.custrecord_cpqc_question_group || q.custrecord_cpqc_question_code)]));
  const sources: Source[] = [];
  for (const raw of ev.sources) {
    const base = { id: raw.id, name: raw.name, kind: raw.kind, createdAt: Number(raw.createdAt), customerId: raw.customerId, user: raw.user, auto: raw.auto, quote: raw.quote };
    if (raw.kind === "cpq2" && raw.configSnapshot) {
      try {
        const snap = await parseConfigSnapshot(raw.configSnapshot);
        if (snap.product !== who.product) continue;
        const flat = flatten(snap.answers, optionOrder);
        sources.push({ ...base, flat, keys: flat });
      } catch {
        /* an unreadable snapshot is simply not evidence */
      }
    } else if (raw.kind === "legacy" && raw.flat) {
      const keys = raw.flat;
      let done: Flat | null = null;
      const s = { ...base, keys } as Source;
      Object.defineProperty(s, "flat", {
        enumerable: true,
        get: () => (done ??= flatten(complete(toAnswers(keys, optionsOf)), optionOrder)),
      });
      sources.push(s);
    }
  }
  const first = visibleTabsInOrder(tabs)[0];
  // The blank form completed: what a legacy order's defaults are measured against.
  const blankAnswers = complete({});
  const blank = flatten(blankAnswers, optionOrder);
  const data = { slug: who.product, tabs, questions };
  session = {
    blank,
    blankAnswers,
    shape: formShape(questions, who.product),
    visible: (state) => visibleQuestionCodes(data as Parameters<typeof visibleQuestionCodes>[0], state),
    gaps: {},
    legacyKeys: model.legacyKeys ? new Set(model.legacyKeys) : undefined,
    ...who,
    model,
    sources,
    pick: sources.find((s) => s.id === ev.pickId) ?? null,
    optionOrder,
    optionsOf,
    groupOf: (q) => groups.get(q) ?? q,
    onFirstPage: first ? keysOnTab(questions, first.id) : () => false,
    launch: launchKeys(questions, tabs),
    scored: scoredKeys(questions, tabs),
    labelOf: (k, v) => {
      if (k.includes("/")) return v;
      const a = questions.find((q) => q.custrecord_cpqc_question_code === k)?._answers?.find((x) => x.custrecord_cpqc_answer_code === v);
      return a ? String((a as { custrecord_cpqc_answer_label_html?: string }).custrecord_cpqc_answer_label_html || a.custrecord_cpqc_answer_code).replace(/<[^>]+>/g, "").trim() : v;
    },
    keyLabel: (k) => {
      const q = k.includes("/") ? k.slice(0, k.indexOf("/")) : k;
      const Q = questions.find((x) => x.custrecord_cpqc_question_code === q);
      const base = String(Q?.custrecord_cpqc_question_label_html || q).replace(/<[^>]+>/g, "").trim();
      const cell = k.includes("/") && k !== `${q}/${q}` ? k.slice(k.indexOf("/") + 1).replace(/_1$/, "").replace(/_/g, " ").toLowerCase() : "";
      return cell ? `${base} (${cell})` : base;
    },
  };
  return session;
}

/** Where a predicted answer came from, in the rep's words, with how sure it is. */
export type Provenance = Record<string, { title: string; unsure: boolean }>;
const COL_WORDS: Record<string, string> = { MAIN: "Main", PILOT: "Pilot", AUX_1: "Aux 1", AUX_2: "Aux 2", AUX_3: "Aux 3", AUX_4: "Aux 4", AUX_5: "Aux 5" };
const SOURCE_WORDS: Record<string, (name: string) => string> = {
  // The gap filler (fieldwise fillGaps): nothing visible is left blank, and the hover says how.
  gapVote: () => "what everyone orders -- no closer history has this answer",
  gapKin: (col) => `the ${COL_WORDS[col] ?? col} cell beside it -- no history has this one`,
  gapKinVote: (col) => `what everyone orders for the ${COL_WORDS[col] ?? col} cell beside it -- no history has this one`,
  gapFirst: () => "the first option offered -- no history has this answer at all. Check it",
  session: (n) => `your previous line, ${n}`,
  exact: (n) => `this customer's ${n}, a repeat of this configuration`,
  pick: (n) => `this customer's ${n}`,
  custModel: (n) => `this customer's last ${n} of this model`,
  custHousing: (n) => `this customer's last ${n} with this housing`,
  custNewest: (n) => `this customer's newest, ${n}`,
  custVote: () => "what this customer usually orders",
  custModelVote: () => "what this customer usually orders in this model",
  repNewest: (n) => `your own ${n}, for another customer`,
  repModel: (n) => `your own ${n}, this model for another customer`,
  everyModel: () => "what everyone orders in this model",
  everyHousing: () => "what everyone orders with this housing",
  everyone: () => "what everyone orders",
  po: (n) => `the customer's ${n} for this order`,
  poQuote: (n) => `the quote ${n} the customer's PO cites`,
  poRepeat: (n) => `this customer's ${n}, which the PO says this order repeats`,
};
export const UNSURE_BELOW = 0.6;
function provenanceOf(c: Choice, ev: Evidence, poNotes: PoNotes = {}): Provenance {
  const names = evidenceNames.get(ev) ?? {};
  const out: Provenance = {};
  for (const [k, src] of Object.entries(c.from)) {
    // A gap fill names what it was guessed from after a colon (gapKin:MAIN).
    const [src0, arg] = src.split(":");
    const base = src0.replace(/~L$/, "").replace(/Old$/, "");
    const words = (SOURCE_WORDS[base] ?? ((n: string) => n))(names[src] ?? arg ?? "");
    const conf = c.confidence[k] ?? 0.5;
    const pctText = `${Math.round(conf * 100)}% of the time`;
    const pn = poNotes[k];
    const note = { title: `Predicted from ${words}${src.endsWith("Old") ? " (over a year old)" : ""}; right ${pctText}.${pn ? pn.extra : ""}`, unsure: conf < UNSURE_BELOW };
    out[k] = note;
    // A select is predicted under its question code; the form asks by the chosen option's key.
    if (!k.includes("/") && c.values[k]) out[`${k}/${c.values[k]}`] = note;
  }
  return out;
}

/** The first page, before anything is chosen. */
export function predictFirstPage(fw: Fieldwise): Choice & { provenance: Provenance } {
  const ev = evidenceFor("p1", fw.sources, {
    customerId: fw.customerId, user: fw.user, now: Date.now(), onFirstPage: fw.onFirstPage,
    everyone: { overall: fw.model.everyone.overall }, launchPick: fw.pick ?? undefined,
  });
  const c0 = choose("p1", ev, (k, s) => reliability(fw.model.rel, "p1", k, s), fw.launch, fw.groupOf);
  const { c: c1, notes } = withPo("p1", fw, ev, c0, {}, fw.launch);
  // Nothing visible left blank (fieldwise fillGaps): the first page over the blank form.
  const g = fillGaps("p1", c1, fw.shape, fw.blankAnswers, ev, { onPage: fw.launch, visible: fw.visible, optionsOf: fw.optionsOf, relOf: (k, s) => reliability(fw.model.rel, "p1", k, s) });
  fw.gaps.p1 = { filled: g.filled, unfilled: g.unfilled };
  const c = g.c;
  return { ...c, provenance: provenanceOf(c, ev, notes) };
}

/** The pages after the first, from the form as it stands (the user's first page and housing). */
export function predictLaterPages(
  fw: Fieldwise,
  answers: AnswerState,
  /** Section mode (util/sectionMode.ts): only these answer keys, from evidence agreeing with
   *  `conditions` (the answers the rep has confirmed). */
  opts?: { onPage?: (k: string) => boolean; conditions?: AnswerState }
): Choice & { provenance: Provenance } {
  const current = flatten(answers, fw.optionOrder);
  const conditions = opts?.conditions ? flatten(opts.conditions, fw.optionOrder) : undefined;
  const ev = evidenceFor("p2", fw.sources, {
    customerId: fw.customerId, user: fw.user, now: Date.now(), onFirstPage: fw.onFirstPage, blank: fw.blank, legacyKeys: fw.legacyKeys, conditions,
    everyone: { product: fw.model.everyone.byProduct[productKey(current)], housing: fw.model.everyone.byHousing[housingKey(current)], overall: fw.model.everyone.overall },
  }, current);
  const onPage = opts?.onPage ?? ((k: string) => fw.scored(k) && !fw.onFirstPage(k));
  const c0 = choose("p2", ev, (k, s) => reliability(fw.model.rel, "p2", k, s), onPage, fw.groupOf);
  const { c: c1, notes } = withPo("p2", fw, ev, c0, current, onPage);
  // Nothing visible left blank (fieldwise fillGaps): the later pages over the form as it stands.
  const g = fillGaps("p2", c1, fw.shape, answers, ev, { onPage, visible: fw.visible, optionsOf: fw.optionsOf, relOf: (k, s) => reliability(fw.model.rel, "p2", k, s) });
  fw.gaps.p2 = { filled: g.filled, unfilled: g.unfilled };
  const c = g.c;
  return { ...c, provenance: provenanceOf(c, ev, notes) };
}

/** Where the prediction came from, in the words the Prediction switch shows. */
export function describeFieldwise(fw: Fieldwise): { name: string; createdAt: string; basis: "customer" | "everyone" } {
  const mine = fw.sources.filter((s) => s.customerId === fw.customerId);
  const newest = mine.sort((a, b) => b.createdAt - a.createdAt)[0] ?? fw.pick;
  return {
    name: mine.length ? `this customer's ${mine.length} past configuration${mine.length === 1 ? "" : "s"}` : "similar configurations",
    createdAt: newest ? new Date(newest.createdAt).toISOString() : "",
    basis: mine.length ? "customer" : "everyone",
  };
}
